import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Eye, ArrowLeft, Activity, ExternalLink, Plus, X, ChevronRight,
  TrendingUp, TrendingDown, Sparkles, Crown, Skull, Flame,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── helpers ──────────────────────────────────────────────── */

function shortAgo(iso) {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    const diff = (Date.now() - t) / 1000;
    if (diff < 60) return `${Math.max(1, Math.floor(diff))}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  } catch { return ''; }
}

const PATTERN_META = {
  PANIC_SELL:   { color: '#ff5e5e', glow: 'rgba(255,94,94,0.55)',  Icon: Skull,      label: 'PANIC SELL'   },
  REBUY_HIGHER: { color: '#ff9b3d', glow: 'rgba(255,155,61,0.45)', Icon: TrendingUp, label: 'REBUY HIGHER' },
  DOUBLE_DOWN:  { color: '#dcc878', glow: 'rgba(220,200,120,0.5)', Icon: Flame,      label: 'DOUBLE DOWN'  },
  DIAMOND_HOLD: { color: '#bfa15a', glow: 'rgba(191,161,90,0.65)', Icon: Crown,      label: 'DIAMOND HOLD' },
  FRESH_ENTRY:  { color: '#7dd0ff', glow: 'rgba(125,208,255,0.4)', Icon: Sparkles,   label: 'FRESH ENTRY'  },
  PARTIAL_SELL: { color: '#c98e85', glow: 'rgba(201,142,133,0.4)', Icon: TrendingDown, label: 'PARTIAL SELL' },
  FULL_EXIT:    { color: '#9a8ba6', glow: 'rgba(154,139,166,0.4)', Icon: X,          label: 'FULL EXIT'    },
};

const ACTION_META = {
  BUY:  { fg: '#5be584', bg: 'rgba(91,229,132,0.10)', border: 'rgba(91,229,132,0.35)' },
  SELL: { fg: '#ff5e5e', bg: 'rgba(255,94,94,0.10)',  border: 'rgba(255,94,94,0.35)' },
};

const SOURCE_LABEL = {
  OPTIN: 'enrolled',
  OATH: 'sworn',
  SCAN: 'judged',
  HOLDER: 'holder',
  SEED: 'seeded',
};

function fmtAmount(n) {
  const v = Number(n) || 0;
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  if (v >= 1) return v.toFixed(2);
  if (v > 0) return v.toFixed(4);
  return '0';
}

/* ─── Rolling counter (counts from 0 → target) ─────────────── */
function RollingCounter({ value, label, accent = '#bfa15a', testid }) {
  const [display, setDisplay] = useState(0);
  const displayRef = useRef(0);
  useEffect(() => { displayRef.current = display; }, [display]);
  useEffect(() => {
    const start = displayRef.current;
    const target = Number(value) || 0;
    if (target === start) return;
    const dur = 900;
    const t0 = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(start + (target - start) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return (
    <div className="flex flex-col items-center text-center" data-testid={testid}>
      <span
        className="font-mono"
        style={{
          fontSize: 'clamp(2.5rem, 5vw, 4rem)',
          lineHeight: 1,
          color: accent,
          textShadow: `0 0 32px ${accent}66`,
          letterSpacing: '0.02em',
          fontWeight: 800,
        }}
      >
        {display.toLocaleString()}
      </span>
      <span
        className="uppercase font-mono mt-2"
        style={{
          fontSize: '0.62rem',
          letterSpacing: '0.32em',
          color: 'rgba(230,220,200,0.5)',
        }}
      >
        {label}
      </span>
    </div>
  );
}

/* ─── Single event row ─────────────────────────────────────── */
function EventRow({ ev, fresh }) {
  const pat = PATTERN_META[ev.pattern] || PATTERN_META.FRESH_ENTRY;
  const act = ACTION_META[ev.action] || ACTION_META.BUY;
  const Icon = pat.Icon;
  const ago = shortAgo(ev.created_at);

  return (
    <div
      data-testid={`watch-event-${ev.event_id}`}
      className="relative grid items-start gap-2 sm:gap-3 px-3 sm:px-5 py-3 sm:py-4 transition-colors grid-cols-[60px_1fr_36px] sm:grid-cols-[52px_96px_1fr_110px]"
      style={{
        background: fresh ? 'rgba(220,200,120,0.07)' : 'transparent',
        borderBottom: '1px solid rgba(220,200,120,0.07)',
        animation: fresh ? 'soul-event-flash 1.6s ease-out' : 'none',
      }}
    >
      {/* Time gutter */}
      <div className="font-mono" style={{ fontSize: '0.68rem', color: 'rgba(230,220,200,0.42)', paddingTop: 6 }}>
        T-{ago}
      </div>

      {/* Wallet — desktop only as separate column; on mobile it goes into the body row */}
      <Link
        to="#"
        onClick={(e) => e.preventDefault()}
        className="hidden sm:inline-flex font-mono uppercase tracking-wider hover:underline"
        style={{
          fontSize: '0.78rem',
          color: '#dcc878',
          letterSpacing: '0.08em',
          paddingTop: 4,
        }}
      >
        {ev.wallet_short}
      </Link>

      {/* Narration body */}
      <div className="flex flex-col gap-1.5 min-w-0">
        <div className="flex items-center flex-wrap gap-1.5 sm:gap-2">
          {/* Mobile-only wallet pill (since it has no dedicated column on small) */}
          <span
            className="sm:hidden font-mono uppercase"
            style={{
              fontSize: '0.62rem',
              color: '#dcc878',
              letterSpacing: '0.06em',
            }}
          >
            {ev.wallet_short}
          </span>
          <span
            className="font-mono uppercase px-2 py-0.5 rounded-sm"
            style={{
              fontSize: '0.6rem',
              letterSpacing: '0.16em',
              color: act.fg,
              background: act.bg,
              border: `1px solid ${act.border}`,
            }}
          >
            {ev.action}
          </span>
          <span className="font-mono" style={{ color: '#e6dcc8', fontSize: '0.82rem' }}>
            {fmtAmount(ev.amount_delta)} ${ev.token_symbol}
          </span>
          <span
            className="font-mono uppercase inline-flex items-center gap-1 px-2 py-0.5 rounded-sm"
            style={{
              fontSize: '0.58rem',
              letterSpacing: '0.16em',
              color: pat.color,
              background: 'rgba(0,0,0,0.4)',
              border: `1px solid ${pat.color}55`,
              boxShadow: `0 0 14px ${pat.glow}`,
            }}
          >
            <Icon size={9} strokeWidth={2.5} />
            {pat.label}
          </span>
        </div>
        <p
          className="italic"
          style={{
            fontSize: '0.92rem',
            lineHeight: 1.45,
            color: 'rgba(230,220,200,0.92)',
            fontFamily: '"EB Garamond", Georgia, serif',
          }}
        >
          {ev.narration}
        </p>
      </div>

      {/* Tx link */}
      <div className="flex justify-end items-start pt-1">
        {ev.tx_signature ? (
          <a
            href={`https://solscan.io/tx/${ev.tx_signature}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono inline-flex items-center gap-1 px-1.5 sm:px-2 py-1 rounded-sm transition-colors hover:bg-white/5"
            style={{ fontSize: '0.6rem', color: 'rgba(230,220,200,0.45)', letterSpacing: '0.12em' }}
            data-testid={`watch-event-tx-${ev.event_id}`}
          >
            <span className="hidden sm:inline">VERIFY</span>
            <ExternalLink size={9} />
          </a>
        ) : null}
      </div>
    </div>
  );
}

/* ─── Opt-in modal ─────────────────────────────────────────── */
function OptInModal({ open, onClose, onSubmitted }) {
  const [wallet, setWallet] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/chancellor/watch/optin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet: wallet.trim() }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || 'Could not enroll');
      setDone(j);
      onSubmitted?.(j);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      style={{ background: 'rgba(5,2,8,0.92)', backdropFilter: 'blur(8px)' }}
      data-testid="watch-optin-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md p-7 rounded-sm"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'linear-gradient(180deg, #0c0610 0%, #0a050d 100%)',
          border: '1px solid rgba(220,200,120,0.4)',
          boxShadow: '0 0 60px rgba(220,200,120,0.14), inset 0 0 60px rgba(0,0,0,0.6)',
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-sm hover:bg-white/5"
          data-testid="watch-optin-close"
        >
          <X size={14} style={{ color: '#dcc878' }} />
        </button>

        <div className="flex items-center gap-2 mb-4">
          <Eye size={14} style={{ color: '#bfa15a' }} />
          <span
            className="font-mono uppercase"
            style={{ fontSize: '0.66rem', letterSpacing: '0.32em', color: '#bfa15a' }}
          >
            Submit a Soul
          </span>
        </div>

        {!done ? (
          <>
            <h2
              className="mb-2"
              style={{
                fontFamily: '"Cinzel", Georgia, serif',
                fontSize: '1.6rem',
                color: '#e6dcc8',
                letterSpacing: '0.04em',
              }}
            >
              The Chancellor will watch
            </h2>
            <p
              className="italic mb-5"
              style={{
                fontFamily: '"EB Garamond", serif',
                fontSize: '0.95rem',
                color: 'rgba(230,220,200,0.65)',
                lineHeight: 1.5,
              }}
            >
              Submit a Solana wallet. Every trade it makes will be narrated in public, on this feed,
              forever. The soul cannot hide.
            </p>

            <form onSubmit={submit} className="flex flex-col gap-3">
              <input
                value={wallet}
                onChange={(e) => setWallet(e.target.value)}
                placeholder="Solana wallet address"
                className="font-mono px-3 py-2.5 rounded-sm outline-none"
                style={{
                  background: 'rgba(0,0,0,0.5)',
                  border: '1px solid rgba(220,200,120,0.3)',
                  color: '#e6dcc8',
                  fontSize: '0.85rem',
                }}
                data-testid="watch-optin-wallet"
              />
              {err ? (
                <span style={{ color: '#ff7777', fontSize: '0.78rem' }} data-testid="watch-optin-error">
                  {err}
                </span>
              ) : null}
              <button
                type="submit"
                disabled={busy || !wallet.trim()}
                className="font-mono uppercase tracking-widest py-2.5 rounded-sm transition-all disabled:opacity-40"
                style={{
                  background: 'linear-gradient(180deg, #2a1a0c 0%, #1a0d04 100%)',
                  border: '1px solid #bfa15a',
                  color: '#dcc878',
                  letterSpacing: '0.2em',
                  fontSize: '0.78rem',
                  boxShadow: '0 0 20px rgba(220,200,120,0.18)',
                }}
                data-testid="watch-optin-submit"
              >
                {busy ? 'Enrolling…' : 'Enroll Soul'}
              </button>
            </form>
          </>
        ) : (
          <div className="text-center py-2" data-testid="watch-optin-success">
            <Eye size={32} style={{ color: '#bfa15a', margin: '0 auto 14px' }} />
            <h3
              style={{
                fontFamily: '"Cinzel", serif',
                fontSize: '1.3rem',
                color: '#dcc878',
                marginBottom: 6,
              }}
            >
              Soul enrolled
            </h3>
            <p
              className="font-mono mb-5"
              style={{ fontSize: '0.78rem', color: 'rgba(230,220,200,0.6)' }}
            >
              {done.wallet_short}
            </p>
            <p
              className="italic"
              style={{
                fontFamily: '"EB Garamond", serif',
                fontSize: '0.92rem',
                color: 'rgba(230,220,200,0.7)',
              }}
            >
              The Chancellor begins his vigil. Refresh this page in a few minutes.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Pattern distribution bar (right column) ──────────────── */
function PatternBars({ patterns }) {
  if (!patterns?.length) {
    return (
      <p className="font-mono italic" style={{ fontSize: '0.78rem', color: 'rgba(230,220,200,0.4)' }}>
        Awaiting first verdicts…
      </p>
    );
  }
  const max = Math.max(...patterns.map((p) => p.count));
  return (
    <div className="flex flex-col gap-2.5" data-testid="watch-pattern-bars">
      {patterns.map((p) => {
        const meta = PATTERN_META[p.pattern] || PATTERN_META.FRESH_ENTRY;
        const pct = (p.count / max) * 100;
        return (
          <div key={p.pattern} className="flex items-center gap-2">
            <span
              className="font-mono uppercase"
              style={{
                fontSize: '0.6rem',
                letterSpacing: '0.14em',
                color: meta.color,
                width: 110,
                flexShrink: 0,
              }}
            >
              {meta.label}
            </span>
            <div className="flex-1 h-2 rounded-sm relative overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <div
                className="absolute inset-y-0 left-0 rounded-sm transition-all duration-700"
                style={{
                  width: `${pct}%`,
                  background: meta.color,
                  boxShadow: `0 0 10px ${meta.glow}`,
                }}
              />
            </div>
            <span
              className="font-mono"
              style={{ fontSize: '0.7rem', color: '#e6dcc8', width: 28, textAlign: 'right' }}
            >
              {p.count}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Main page ────────────────────────────────────────────── */
const PATTERN_FILTER_KEYS = [
  'PANIC_SELL', 'REBUY_HIGHER', 'DOUBLE_DOWN',
  'DIAMOND_HOLD', 'FRESH_ENTRY', 'PARTIAL_SELL', 'FULL_EXIT',
];

export default function WatchedSoulsPage() {
  const [stats, setStats] = useState({ souls_watched: 0, events_total: 0, events_24h: 0, patterns: [] });
  const [events, setEvents] = useState([]);
  const [souls, setSouls] = useState([]);
  const [coords, setCoords] = useState([]);
  const [optInOpen, setOptInOpen] = useState(false);
  // Filter state — these are sent server-side for cursor pagination support.
  const [actionFilter, setActionFilter] = useState('ALL');         // ALL | BUY | SELL
  const [patternFilter, setPatternFilter] = useState(null);        // null | PANIC_SELL ...
  const [walletFilter, setWalletFilter] = useState('');            // free text
  const [tokenFilter, setTokenFilter] = useState('');              // free text symbol/mint
  const [walletInput, setWalletInput] = useState('');
  const [tokenInput, setTokenInput] = useState('');
  // Pagination
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [exhausted, setExhausted] = useState(false);

  const lastSeenIdRef = useRef(null);
  const [freshIds, setFreshIds] = useState(new Set());

  // Build query string from current filters
  const buildFeedQuery = (extra = {}) => {
    const params = new URLSearchParams({ limit: '80' });
    if (actionFilter !== 'ALL') params.set('action', actionFilter);
    if (patternFilter) params.set('pattern', patternFilter);
    if (walletFilter) params.set('wallet', walletFilter);
    if (tokenFilter) params.set('token', tokenFilter);
    Object.entries(extra).forEach(([k, v]) => v && params.set(k, v));
    return params.toString();
  };

  // Live tick: top of feed. Resets pagination cursor to "newest".
  const load = async () => {
    try {
      const [s, f, w, c] = await Promise.all([
        fetch(`${API_URL}/api/chancellor/watch/stats`).then((r) => r.json()),
        fetch(`${API_URL}/api/chancellor/watch/feed?${buildFeedQuery()}`).then((r) => r.json()),
        fetch(`${API_URL}/api/chancellor/watch/souls?limit=12`).then((r) => r.json()),
        fetch(`${API_URL}/api/chancellor/watch/coordination?limit=8`).then((r) => r.json()),
      ]);
      setStats(s || {});
      const list = f?.events || [];
      // Mark fresh events (not seen on previous tick)
      if (lastSeenIdRef.current && list.length) {
        const idx = list.findIndex((e) => e.event_id === lastSeenIdRef.current);
        if (idx > 0) {
          const fresh = new Set(list.slice(0, idx).map((e) => e.event_id));
          setFreshIds(fresh);
          setTimeout(() => setFreshIds(new Set()), 1800);
        }
      }
      lastSeenIdRef.current = list[0]?.event_id || lastSeenIdRef.current;
      setEvents(list);
      setNextCursor(f?.next_cursor || null);
      setExhausted(!f?.next_cursor);
      setSouls(w?.souls || []);
      setCoords(c?.events || []);
    } catch {/* silent */}
  };

  const loadOlder = async () => {
    if (loadingOlder || !nextCursor) return;
    setLoadingOlder(true);
    try {
      const r = await fetch(
        `${API_URL}/api/chancellor/watch/feed?${buildFeedQuery({ before: nextCursor })}`
      ).then((x) => x.json());
      const more = r?.events || [];
      if (more.length === 0) {
        setExhausted(true);
        setNextCursor(null);
      } else {
        // Dedupe by event_id (cursor boundary may overlap)
        setEvents((prev) => {
          const seen = new Set(prev.map((e) => e.event_id));
          return [...prev, ...more.filter((e) => !seen.has(e.event_id))];
        });
        setNextCursor(r?.next_cursor || null);
        setExhausted(!r?.next_cursor);
      }
    } catch {/* silent */}
    finally { setLoadingOlder(false); }
  };

  const loadRef = useRef(load);
  loadRef.current = load;

  // Re-fetch from top whenever any server-side filter changes
  useEffect(() => {
    loadRef.current();
    const t = setInterval(() => loadRef.current(), 25000);
    return () => clearInterval(t);
  }, [actionFilter, patternFilter, walletFilter, tokenFilter]);

  // Refresh IMMEDIATELY when the user returns to the tab/window. Browsers throttle
  // setInterval in background tabs (often to once per minute), so without this the
  // feed can look stale for up to 25s after focus returns.
  useEffect(() => {
    const handleVisible = () => {
      if (!document.hidden) loadRef.current();
    };
    const handleFocus = () => loadRef.current();
    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const applyWalletInput = () => setWalletFilter(walletInput.trim());
  const applyTokenInput = () => setTokenFilter(tokenInput.trim());
  const clearAllFilters = () => {
    setActionFilter('ALL');
    setPatternFilter(null);
    setWalletFilter('');
    setTokenFilter('');
    setWalletInput('');
    setTokenInput('');
  };

  const hasActiveFilter =
    actionFilter !== 'ALL' || patternFilter || walletFilter || tokenFilter;

  // Server already filters; we just render events as-is.
  const filtered = events;

  return (
    <div
      className="relative min-h-screen w-full overflow-x-clip"
      data-testid="watched-souls-page"
      style={{
        background: '#050208',
        color: '#e6dcc8',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      <style>{`
        @keyframes soul-event-flash {
          0%   { background: rgba(220,200,120,0.32); box-shadow: inset 0 0 30px rgba(220,200,120,0.4); }
          60%  { background: rgba(220,200,120,0.10); }
          100% { background: rgba(220,200,120,0.07); box-shadow: none; }
        }
        @keyframes soul-cursor-blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        @keyframes soul-scanline {
          0%   { transform: translateY(-2vh); }
          100% { transform: translateY(102vh); }
        }
        @keyframes soul-grain {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(-1%, 1%); }
          50% { transform: translate(1%, -1%); }
          75% { transform: translate(-1%, -1%); }
        }
        @keyframes soul-watching-pulse {
          0%, 100% { opacity: 0.55; }
          50% { opacity: 1; }
        }
        .soul-feed-scroll::-webkit-scrollbar { width: 6px; }
        .soul-feed-scroll::-webkit-scrollbar-track { background: rgba(255,255,255,0.02); }
        .soul-feed-scroll::-webkit-scrollbar-thumb { background: rgba(220,200,120,0.18); border-radius: 3px; }
        .soul-feed-scroll::-webkit-scrollbar-thumb:hover { background: rgba(220,200,120,0.32); }
      `}</style>

      {/* Atmospheric layers */}
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          background:
            'radial-gradient(circle at 20% 10%, rgba(120,30,30,0.18) 0%, transparent 55%), ' +
            'radial-gradient(circle at 80% 90%, rgba(40,10,30,0.22) 0%, transparent 55%), ' +
            'linear-gradient(180deg, #050208 0%, #0a0410 60%, #050208 100%)',
        }}
      />
      {/* Grain */}
      <div
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.10] mix-blend-overlay"
        style={{
          backgroundImage:
            'url("data:image/svg+xml;utf8,<svg xmlns=\\"http://www.w3.org/2000/svg\\" width=\\"180\\" height=\\"180\\"><filter id=\\"n\\"><feTurbulence baseFrequency=\\"0.85\\"/></filter><rect width=\\"100%\\" height=\\"100%\\" filter=\\"url(%23n)\\" opacity=\\"0.6\\"/></svg>")',
          animation: 'soul-grain 8s steps(4) infinite',
        }}
      />
      {/* Scanline */}
      <div
        className="pointer-events-none fixed inset-x-0 z-0"
        style={{
          height: '2px',
          background: 'linear-gradient(90deg, transparent 0%, rgba(220,200,120,0.5) 50%, transparent 100%)',
          animation: 'soul-scanline 9s linear infinite',
          opacity: 0.35,
        }}
      />

      {/* ── Top bar ─────────────────────────────────────────── */}
      <div
        className="sticky top-0 z-20 flex items-center justify-between px-3 sm:px-6 lg:px-10 pr-16 md:pr-6 lg:pr-10 py-3 sm:py-4 border-b gap-2"
        style={{
          borderColor: 'rgba(220,200,120,0.18)',
          background: 'rgba(5,2,8,0.85)',
          backdropFilter: 'blur(6px)',
        }}
      >
        <Link
          to="/halls"
          className="inline-flex items-center gap-1.5 sm:gap-2 font-mono uppercase tracking-[0.22em] sm:tracking-[0.28em] transition-all hover:scale-[1.04] shrink-0"
          style={{
            padding: '7px 11px',
            fontSize: '0.62rem',
            color: '#ffd680',
            background: 'linear-gradient(180deg, rgba(40,12,8,0.6) 0%, rgba(20,6,4,0.85) 100%)',
            border: '1px solid rgba(200,144,72,0.55)',
            borderRadius: 2,
            boxShadow: '0 0 14px rgba(200,144,72,0.28), inset 0 0 14px rgba(0,0,0,0.5)',
            textDecoration: 'none',
            textShadow: '0 0 8px rgba(200,144,72,0.5)',
          }}
          data-testid="watch-back-home"
        >
          <ArrowLeft size={12} className="sm:hidden" />
          <ArrowLeft size={13} className="hidden sm:inline" />
          <span className="hidden sm:inline">Halls of the Chancellor</span>
          <span className="sm:hidden">Halls</span>
        </Link>

        {/* Desktop only: LIVE indicator (centered absolutely) */}
        <div
          className="hidden sm:flex items-center gap-2 absolute left-1/2 top-1/2"
          style={{ transform: 'translate(-50%, -50%)' }}
        >
          <div
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: '#5be584', boxShadow: '0 0 10px #5be584', animation: 'soul-watching-pulse 1.5s ease-in-out infinite' }}
          />
          <span
            className="font-mono uppercase"
            style={{ fontSize: '0.62rem', letterSpacing: '0.32em', color: 'rgba(230,220,200,0.65)' }}
          >
            LIVE • Autonomous
          </span>
        </div>

        {/* Mobile only: Submit Soul centered within bar's content area (accounts for px-3 / pr-16 asymmetry) */}
        <button
          onClick={() => setOptInOpen(true)}
          className="sm:hidden font-mono uppercase tracking-widest absolute top-1/2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm transition-all"
          style={{
            background: 'linear-gradient(180deg, #2a1a0c 0%, #1a0d04 100%)',
            border: '1px solid #bfa15a',
            color: '#dcc878',
            letterSpacing: '0.22em',
            fontSize: '0.66rem',
            boxShadow: '0 0 15px rgba(220,200,120,0.18)',
            // Visual center between HALLS link (~95px right edge) and hamburger (~60px from right):
            // ((95) + (vw - 60)) / 2 = vw/2 + 17.5
            left: 'calc(50% + 18px)',
            transform: 'translate(-50%, -50%)',
          }}
          data-testid="watch-optin-cta-mobile"
        >
          <Plus size={11} />
          Submit Soul
        </button>

        {/* (mobile spacer removed — absolute-positioned button doesn't need flex balance) */}

        {/* Desktop only: Submit Soul on right */}
        <button
          onClick={() => setOptInOpen(true)}
          className="hidden sm:inline-flex font-mono uppercase tracking-widest items-center gap-1.5 px-3 py-1.5 rounded-sm transition-all hover:scale-[1.02]"
          style={{
            background: 'linear-gradient(180deg, #2a1a0c 0%, #1a0d04 100%)',
            border: '1px solid #bfa15a',
            color: '#dcc878',
            letterSpacing: '0.22em',
            fontSize: '0.66rem',
            boxShadow: '0 0 15px rgba(220,200,120,0.18)',
          }}
          data-testid="watch-optin-cta"
        >
          <Plus size={11} />
          Submit Soul
        </button>
      </div>

      {/* ── Hero ────────────────────────────────────────────── */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-10 pt-8 sm:pt-10 pb-6">
        <div className="flex items-center gap-3 mb-3">
          <Eye size={14} style={{ color: '#bfa15a' }} />
          <span
            className="font-mono uppercase"
            style={{ fontSize: '0.7rem', letterSpacing: '0.36em', color: '#bfa15a' }}
          >
            The Chancellor's Vigil
          </span>
        </div>
        <h1
          className="mb-3"
          style={{
            fontFamily: '"Cinzel", Georgia, serif',
            fontSize: 'clamp(2.4rem, 6vw, 4.4rem)',
            lineHeight: 1.05,
            color: '#e6dcc8',
            letterSpacing: '0.02em',
          }}
        >
          Watched Souls
          <span
            className="inline-block ml-2"
            style={{
              animation: 'soul-cursor-blink 1.1s steps(1) infinite',
              color: '#bfa15a',
            }}
          >
            ▌
          </span>
        </h1>
        <p
          className="italic max-w-3xl"
          style={{
            fontFamily: '"EB Garamond", Georgia, serif',
            fontSize: 'clamp(1rem, 1.4vw, 1.15rem)',
            lineHeight: 1.55,
            color: 'rgba(230,220,200,0.72)',
          }}
        >
          The Chancellor watches. He chooses souls from oaths sworn, scans demanded, and holders of conviction.
          Every trade is narrated as it happens. No human types these words. Only the chain, and the AI that judges it.
        </p>
      </div>

      {/* ── Coordination Banner — cross-wallet detection ──── */}
      {coords.length > 0 ? (
        <div
          className="relative z-10 mx-3 sm:mx-6 lg:mx-10 mb-6"
          data-testid="watch-coordination-banner"
        >
          <div
            className="relative px-6 py-5 rounded-sm overflow-hidden"
            style={{
              background:
                'linear-gradient(135deg, rgba(120,30,60,0.18) 0%, rgba(60,20,80,0.12) 50%, rgba(20,10,40,0.18) 100%)',
              border: '1px solid rgba(220,90,140,0.4)',
              boxShadow: '0 0 28px rgba(220,90,140,0.18), inset 0 0 60px rgba(0,0,0,0.5)',
            }}
          >
            {/* Pulsing strings background */}
            <div
              className="pointer-events-none absolute inset-0 opacity-40"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(120deg, transparent 0 18px, rgba(220,90,140,0.10) 18px 20px)',
                animation: 'soul-watching-pulse 2.5s ease-in-out infinite',
              }}
            />
            <div className="relative flex items-center gap-3 mb-3">
              <span
                className="inline-flex items-center justify-center w-7 h-7 rounded-full"
                style={{
                  background: 'rgba(220,90,140,0.18)',
                  border: '1px solid rgba(220,90,140,0.55)',
                  boxShadow: '0 0 12px rgba(220,90,140,0.35)',
                }}
              >
                <Sparkles size={13} style={{ color: '#ff7eb6' }} />
              </span>
              <span
                className="font-mono uppercase"
                style={{
                  fontSize: '0.66rem',
                  letterSpacing: '0.32em',
                  color: '#ff7eb6',
                  textShadow: '0 0 10px rgba(220,90,140,0.4)',
                }}
              >
                The Strings Are Visible
              </span>
              <span
                className="font-mono uppercase ml-auto"
                style={{
                  fontSize: '0.6rem',
                  letterSpacing: '0.28em',
                  color: 'rgba(255,126,182,0.55)',
                }}
              >
                {coords.length} {coords.length === 1 ? 'pattern' : 'patterns'}
              </span>
            </div>

            <div className="relative flex flex-col gap-3" data-testid="watch-coordination-list">
              {coords.slice(0, 3).map((c) => {
                const isSwarm = c.pattern === 'SWARM_BUY' || c.pattern === 'SWARM_EXIT';
                const isExit = c.action === 'SELL';
                const tagColor = isExit ? '#ff5e5e' : '#7dffb6';
                return (
                  <div
                    key={c.coord_id}
                    className="grid items-start gap-2 sm:gap-3 grid-cols-[88px_1fr] sm:grid-cols-[110px_1fr_auto]"
                    data-testid={`watch-coord-${c.coord_id}`}
                  >
                    <div className="flex flex-col gap-1">
                      <span
                        className="font-mono uppercase px-2 py-0.5 rounded-sm text-center"
                        style={{
                          fontSize: '0.58rem',
                          letterSpacing: '0.16em',
                          color: tagColor,
                          background: 'rgba(0,0,0,0.5)',
                          border: `1px solid ${tagColor}55`,
                          boxShadow: `0 0 10px ${tagColor}22`,
                        }}
                      >
                        {c.pattern.replace('_', ' ')}
                      </span>
                      <span
                        className="font-mono"
                        style={{ fontSize: '0.62rem', color: 'rgba(230,220,200,0.5)', textAlign: 'center' }}
                      >
                        {c.wallet_count} souls
                      </span>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="font-mono"
                          style={{ fontSize: '0.78rem', color: '#dcc878', letterSpacing: '0.04em' }}
                        >
                          ${c.token_symbol}
                        </span>
                        {(c.wallets_short || []).slice(0, 4).map((ws) => (
                          <span
                            key={ws}
                            className="font-mono uppercase"
                            style={{
                              fontSize: '0.58rem',
                              letterSpacing: '0.08em',
                              color: 'rgba(220,200,120,0.55)',
                              padding: '1px 6px',
                              background: 'rgba(255,255,255,0.04)',
                              borderRadius: 2,
                            }}
                          >
                            {ws}
                          </span>
                        ))}
                        {(c.wallets_short || []).length > 4 ? (
                          <span
                            className="font-mono"
                            style={{ fontSize: '0.6rem', color: 'rgba(220,200,120,0.4)' }}
                          >
                            +{c.wallets_short.length - 4}
                          </span>
                        ) : null}
                      </div>
                      <p
                        className="italic"
                        style={{
                          fontFamily: '"EB Garamond", Georgia, serif',
                          fontSize: '0.92rem',
                          lineHeight: 1.4,
                          color: 'rgba(255,210,225,0.92)',
                        }}
                      >
                        {c.narration}
                      </p>
                    </div>
                    <span
                      className="hidden sm:inline font-mono"
                      style={{ fontSize: '0.62rem', color: 'rgba(230,220,200,0.4)', paddingTop: 4 }}
                    >
                      T-{shortAgo(c.updated_at || c.created_at)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      {/* ── Scoreboard ──────────────────────────────────────── */}
      <div
        className="relative z-10 mx-3 sm:mx-6 lg:mx-10 mb-8 grid gap-4"
        style={{
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          justifyItems: 'center',
          padding: '20px 22px',
          background: 'linear-gradient(180deg, rgba(220,200,120,0.06) 0%, rgba(0,0,0,0) 100%)',
          border: '1px solid rgba(220,200,120,0.18)',
          borderRadius: 2,
        }}
        data-testid="watch-scoreboard"
      >
        <RollingCounter value={stats.souls_watched} label="Souls Watched"  accent="#dcc878" testid="stat-souls" />
        <RollingCounter value={stats.events_24h}    label="Events / 24h"   accent="#bfa15a" testid="stat-events-24h" />
        <RollingCounter value={stats.events_total}  label="Lifetime Events" accent="#a08850" testid="stat-events-total" />
        <div className="flex flex-col items-center" data-testid="stat-polling">
          <span
            className="inline-flex items-center justify-start"
            style={{
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              lineHeight: 1,
              height: 'clamp(2.5rem, 5vw, 4rem)',
              color: '#5be584',
              filter: 'drop-shadow(0 0 16px rgba(91,229,132,0.55))',
            }}
          >
            <Activity
              style={{ width: '0.85em', height: '0.85em', strokeWidth: 1.6 }}
              className="animate-pulse"
            />
          </span>
          <span
            className="uppercase font-mono mt-2"
            style={{ fontSize: '0.62rem', letterSpacing: '0.32em', color: 'rgba(230,220,200,0.5)' }}
          >
            Polling · 60s
          </span>
        </div>
      </div>

      {/* ── Main grid: feed + side rail ─────────────────────── */}
      <div
        className="relative z-10 grid gap-6 px-3 sm:px-6 lg:px-10 pb-16 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,300px)]"
      >
        {/* FEED (left) */}
        <div
          className="rounded-sm"
          style={{
            background: 'rgba(0,0,0,0.45)',
            border: '1px solid rgba(220,200,120,0.18)',
            boxShadow: 'inset 0 0 80px rgba(0,0,0,0.5)',
          }}
        >
          {/* Filter strip */}
          <div
            className="border-b"
            style={{ borderColor: 'rgba(220,200,120,0.12)' }}
            data-testid="watch-filter-strip"
          >
            {/* Top row: action chips + entry count */}
            <div className="flex items-center justify-between px-3 sm:px-5 py-3 flex-wrap gap-2">
              <div className="flex items-center gap-1 flex-wrap">
                {['ALL', 'BUY', 'SELL'].map((f) => (
                  <button
                    key={f}
                    onClick={() => setActionFilter(f)}
                    className="font-mono uppercase px-3 py-1 rounded-sm transition-all"
                    style={{
                      fontSize: '0.62rem',
                      letterSpacing: '0.18em',
                      background: actionFilter === f ? 'rgba(220,200,120,0.14)' : 'transparent',
                      border: actionFilter === f ? '1px solid rgba(220,200,120,0.4)' : '1px solid transparent',
                      color: actionFilter === f ? '#dcc878' : 'rgba(230,220,200,0.55)',
                    }}
                    data-testid={`watch-filter-${f.toLowerCase()}`}
                  >
                    {f}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3">
                {hasActiveFilter && (
                  <button
                    onClick={clearAllFilters}
                    className="font-mono uppercase px-2 py-1 rounded-sm transition-colors hover:bg-white/5"
                    style={{
                      fontSize: '0.58rem',
                      letterSpacing: '0.22em',
                      color: 'rgba(255,94,94,0.7)',
                      border: '1px solid rgba(255,94,94,0.3)',
                    }}
                    data-testid="watch-filter-clear"
                  >
                    Clear ×
                  </button>
                )}
                <span
                  className="font-mono uppercase"
                  style={{ fontSize: '0.6rem', letterSpacing: '0.24em', color: 'rgba(230,220,200,0.4)' }}
                >
                  {filtered.length} entries
                </span>
              </div>
            </div>

            {/* Pattern chips */}
            <div className="flex items-center gap-1 flex-wrap px-3 sm:px-5 pb-3">
              {PATTERN_FILTER_KEYS.map((p) => {
                const meta = PATTERN_META[p];
                const active = patternFilter === p;
                return (
                  <button
                    key={p}
                    onClick={() => setPatternFilter(active ? null : p)}
                    className="font-mono uppercase inline-flex items-center gap-1 px-2 py-0.5 rounded-sm transition-all"
                    style={{
                      fontSize: '0.55rem',
                      letterSpacing: '0.16em',
                      color: active ? '#050208' : meta.color,
                      background: active ? meta.color : 'rgba(0,0,0,0.4)',
                      border: `1px solid ${meta.color}${active ? 'ff' : '55'}`,
                      boxShadow: active ? `0 0 16px ${meta.glow}` : 'none',
                    }}
                    data-testid={`watch-pattern-chip-${p}`}
                  >
                    <meta.Icon size={9} strokeWidth={2.5} />
                    {meta.label}
                  </button>
                );
              })}
            </div>

            {/* Wallet & Token search */}
            <div className="flex items-stretch gap-2 px-3 sm:px-5 pb-3 flex-wrap">
              <div className="flex items-center gap-1" style={{ flex: '1 1 240px', minWidth: 200 }}>
                <input
                  value={walletInput}
                  onChange={(e) => setWalletInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applyWalletInput()}
                  placeholder="wallet address…"
                  className="font-mono px-2 py-1 outline-none flex-1 min-w-0"
                  style={{
                    fontSize: '0.7rem',
                    background: 'rgba(0,0,0,0.5)',
                    border: '1px solid rgba(220,200,120,0.18)',
                    color: '#e6dcc8',
                    letterSpacing: '0.04em',
                  }}
                  data-testid="watch-wallet-input"
                />
                <button
                  onClick={applyWalletInput}
                  className="font-mono uppercase px-2 py-1 rounded-sm transition-colors hover:bg-white/5"
                  style={{
                    fontSize: '0.58rem',
                    letterSpacing: '0.22em',
                    color: walletFilter ? '#dcc878' : 'rgba(230,220,200,0.5)',
                    border: '1px solid rgba(220,200,120,0.25)',
                  }}
                  data-testid="watch-wallet-apply"
                >
                  Filter
                </button>
              </div>
              <div className="flex items-center gap-1" style={{ flex: '1 1 180px', minWidth: 160 }}>
                <input
                  value={tokenInput}
                  onChange={(e) => setTokenInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applyTokenInput()}
                  placeholder="token symbol/mint…"
                  className="font-mono px-2 py-1 outline-none flex-1 min-w-0"
                  style={{
                    fontSize: '0.7rem',
                    background: 'rgba(0,0,0,0.5)',
                    border: '1px solid rgba(220,200,120,0.18)',
                    color: '#e6dcc8',
                    letterSpacing: '0.04em',
                  }}
                  data-testid="watch-token-input"
                />
                <button
                  onClick={applyTokenInput}
                  className="font-mono uppercase px-2 py-1 rounded-sm transition-colors hover:bg-white/5"
                  style={{
                    fontSize: '0.58rem',
                    letterSpacing: '0.22em',
                    color: tokenFilter ? '#dcc878' : 'rgba(230,220,200,0.5)',
                    border: '1px solid rgba(220,200,120,0.25)',
                  }}
                  data-testid="watch-token-apply"
                >
                  Filter
                </button>
              </div>
            </div>
          </div>

          {/* Tape */}
          <div
            className="soul-feed-scroll overflow-y-auto"
            style={{ maxHeight: 'calc(100vh - 140px)', minHeight: 540 }}
            data-testid="watch-feed-list"
          >
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
                <Eye size={36} style={{ color: 'rgba(220,200,120,0.4)', marginBottom: 16 }} />
                <p
                  className="italic mb-2"
                  style={{
                    fontFamily: '"EB Garamond", serif',
                    color: 'rgba(230,220,200,0.55)',
                    fontSize: '1.1rem',
                  }}
                >
                  {hasActiveFilter
                    ? 'No narrations match these filters.'
                    : 'The Chancellor begins his vigil…'}
                </p>
                <p className="font-mono" style={{ fontSize: '0.72rem', color: 'rgba(230,220,200,0.35)' }}>
                  {hasActiveFilter
                    ? 'Loosen the filters or wait for fresh souls to act.'
                    : 'First narrations within minutes. Submit a soul to seed the feed.'}
                </p>
              </div>
            ) : (
              <>
                {filtered.map((ev) => (
                  <EventRow key={ev.event_id} ev={ev} fresh={freshIds.has(ev.event_id)} />
                ))}
                {/* Load older — cursor pagination */}
                <div
                  className="flex items-center justify-center py-5 border-t"
                  style={{ borderColor: 'rgba(220,200,120,0.07)' }}
                >
                  {exhausted ? (
                    <span
                      className="font-mono uppercase italic"
                      style={{
                        fontSize: '0.62rem',
                        letterSpacing: '0.24em',
                        color: 'rgba(230,220,200,0.32)',
                      }}
                      data-testid="watch-feed-exhausted"
                    >
                      End of the chronicle
                    </span>
                  ) : (
                    <button
                      onClick={loadOlder}
                      disabled={loadingOlder || !nextCursor}
                      className="font-mono uppercase px-4 py-2 rounded-sm transition-all hover:bg-white/5 disabled:opacity-40"
                      style={{
                        fontSize: '0.62rem',
                        letterSpacing: '0.28em',
                        color: '#dcc878',
                        border: '1px solid rgba(220,200,120,0.3)',
                      }}
                      data-testid="watch-feed-load-older"
                    >
                      {loadingOlder ? 'Summoning…' : 'Load Older Verdicts'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* SIDE RAIL (right) */}
        <div className="flex flex-col gap-6">
          {/* Pattern distribution */}
          <div
            className="p-5 rounded-sm"
            style={{
              background: 'rgba(0,0,0,0.45)',
              border: '1px solid rgba(220,200,120,0.18)',
            }}
            data-testid="watch-patterns-card"
          >
            <h3
              className="font-mono uppercase mb-4"
              style={{
                fontSize: '0.66rem',
                letterSpacing: '0.32em',
                color: '#bfa15a',
              }}
            >
              The Verdicts
            </h3>
            <PatternBars patterns={stats.patterns} />
          </div>

          {/* Watched souls leaderboard */}
          <div
            className="p-5 rounded-sm"
            style={{
              background: 'rgba(0,0,0,0.45)',
              border: '1px solid rgba(220,200,120,0.18)',
            }}
            data-testid="watch-souls-card"
          >
            <h3
              className="font-mono uppercase mb-4"
              style={{
                fontSize: '0.66rem',
                letterSpacing: '0.32em',
                color: '#bfa15a',
              }}
            >
              The Watched ({souls.length})
            </h3>
            {souls.length === 0 ? (
              <p
                className="font-mono italic"
                style={{ fontSize: '0.78rem', color: 'rgba(230,220,200,0.4)' }}
              >
                No souls enrolled yet.
              </p>
            ) : (
              <div className="flex flex-col gap-2.5">
                {souls.map((s) => (
                  <div
                    key={s.wallet}
                    className="flex items-center justify-between gap-2 py-1.5 border-b last:border-b-0"
                    style={{ borderColor: 'rgba(220,200,120,0.06)' }}
                    data-testid={`watch-soul-${s.wallet}`}
                  >
                    <div className="flex flex-col min-w-0">
                      <span
                        className="font-mono truncate"
                        style={{ fontSize: '0.78rem', color: '#dcc878', letterSpacing: '0.06em' }}
                      >
                        {s.wallet_short}
                      </span>
                      <span
                        className="font-mono uppercase"
                        style={{
                          fontSize: '0.55rem',
                          letterSpacing: '0.18em',
                          color: 'rgba(230,220,200,0.4)',
                        }}
                      >
                        {SOURCE_LABEL[s.source] || s.source?.toLowerCase()}
                      </span>
                    </div>
                    <span
                      className="font-mono"
                      style={{ fontSize: '0.72rem', color: '#e6dcc8' }}
                    >
                      {s.event_count || 0}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={() => setOptInOpen(true)}
              className="font-mono uppercase mt-4 w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-sm transition-all hover:bg-white/5"
              style={{
                fontSize: '0.62rem',
                letterSpacing: '0.22em',
                color: '#bfa15a',
                border: '1px dashed rgba(220,200,120,0.4)',
              }}
              data-testid="watch-side-optin-cta"
            >
              <Plus size={11} />
              Add a Soul
            </button>
          </div>

          {/* How it works (footer credibility) */}
          <div
            className="p-5 rounded-sm"
            style={{
              background: 'rgba(0,0,0,0.3)',
              border: '1px solid rgba(220,200,120,0.10)',
            }}
          >
            <h3
              className="font-mono uppercase mb-3"
              style={{ fontSize: '0.62rem', letterSpacing: '0.28em', color: 'rgba(230,220,200,0.5)' }}
            >
              How It Works
            </h3>
            <ol
              className="flex flex-col gap-2"
              style={{
                fontFamily: '"EB Garamond", serif',
                fontSize: '0.85rem',
                color: 'rgba(230,220,200,0.6)',
                lineHeight: 1.4,
                listStyle: 'none',
                paddingLeft: 0,
              }}
            >
              <li><span style={{ color: '#bfa15a' }}>I.</span> Souls enter the watch through oaths sworn, scans demanded, or public submission.</li>
              <li><span style={{ color: '#bfa15a' }}>II.</span> Every minute, each wallet is checked on Solana for new trades.</li>
              <li><span style={{ color: '#bfa15a' }}>III.</span> Trades are sorted by their nature. Panic sells. Diamond holds. Mirror exits. Swarm buys.</li>
              <li><span style={{ color: '#bfa15a' }}>IV.</span> The Chancellor writes about each one in his own voice, live, as it happens.</li>
              <li><span style={{ color: '#bfa15a' }}>V.</span> Every line is bound to a real on-chain signature. Verify it yourself if you doubt him.</li>
              <li style={{ color: 'rgba(230,220,200,0.55)', fontStyle: 'italic', marginTop: 4 }}>
                <span style={{ color: 'rgba(91,229,132,0.6)' }}>VI.</span> Spoken narration is on the way. The Chancellor will soon have a voice you can hear.
              </li>
            </ol>
          </div>

          <Link
            to="/court"
            className="font-mono uppercase inline-flex items-center justify-between px-3 py-2.5 rounded-sm transition-all hover:bg-white/5"
            style={{
              fontSize: '0.66rem',
              letterSpacing: '0.22em',
              color: '#bfa15a',
              border: '1px solid rgba(220,200,120,0.18)',
            }}
            data-testid="watch-court-link"
          >
            Visit the Court
            <ChevronRight size={12} />
          </Link>
        </div>
      </div>

      <OptInModal open={optInOpen} onClose={() => setOptInOpen(false)} onSubmitted={() => { setTimeout(load, 800); }} />
    </div>
  );
}
